import { Component, Inject, Optional, OnInit } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { HttpResponse } from '@angular/common/http';
import { IHistoricalEvents } from 'src/app/interface/IHistoricalEvents.interface';
import { IHistoricalResponse } from 'src/app/interface/IHistoricalResponse.interface';
import { Card } from 'src/app/model/card.model';
import { User } from 'src/app/model/user.model';
import { ApiHappylandService } from 'src/app/services/api-happyland/api-happyland.service';
import { ApiTrackingService } from 'src/app/services/api-tracking/api-tracking.service';
import { HelperService } from 'src/app/services/helper.service';
import { AppSettings } from 'src/app/services/app-settings/app-settings.service';

export interface HistoricalBottomSheetData {
  type: 'gameplay' | 'tickets';
}

@Component({
  selector: 'app-historical-bottom-sheet',
  templateUrl: './historical-bottom-sheet.component.html',
  styleUrls: ['./historical-bottom-sheet.component.scss'],
})
export class HistoricalBottomSheetComponent implements OnInit {
  // Datos históricos
  cardInformation!: { card: Card; enroll?: User };
  sourceETickets: IHistoricalEvents[] = [];
  sourceGames: IHistoricalEvents[] = [];
  cardActiveFrom: string = '';
  lastTabSelected: number = 0;
  isLoadingResults: boolean = true;
  eventFailure: boolean = false;
  isDisabledByAdmin: boolean = false;

  constructor(
    @Optional() public bottomSheetRef: MatBottomSheetRef<HistoricalBottomSheetComponent>,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public data: HistoricalBottomSheetData,
    private appSettings: AppSettings,
    private happylandService: ApiHappylandService,
    private helperService: HelperService,
    private trackingService: ApiTrackingService
  ) {
    this.cardInformation = JSON.parse(localStorage.getItem('cardInformation'));
  }

  ngOnInit(): void {
    // Determinar tab inicial basado en el tipo de datos
    if (this.data && this.data.type) {
      this.lastTabSelected = this.data.type === 'gameplay' ? 0 : 1;
    }

    // Habilitar menus del header
    this.appSettings.appWithLoyaltyCard = !!this.cardInformation.enroll;

    // Solicitar datos históricos
    this.requestHistoricalData(this.cardInformation.card.accountNumber);

    // Actualizar tracking
    this.trackingService.updateSessionOnlyStep(this.lastTabSelected === 0 ? 'PORTAL HISTORICO - TAB JUGADAS' : 'PORTAL HISTORICO - TAB ETICKETS');
  }

  /**
   * Actualiza el tab seleccionado y el tracking
   */
  updateTabEvent(event: any): void {
    this.lastTabSelected = event;
    this.trackingService.updateSessionOnlyStep(this.lastTabSelected === 0 ? 'PORTAL HISTORICO - TAB JUGADAS' : 'PORTAL HISTORICO - TAB ETICKETS');
  }

  /**
   * Formatea el número de tarjeta con guiones
   */
  formatCreditCardNumber(cardNumber: string): string {
    const paddedCardNumber = cardNumber.padStart(16, '0');
    return paddedCardNumber.replace(/(\d{4})(?=\d)/g, '$1-');
  }

  /**
   * Solicita los datos históricos de la tarjeta
   */
  async requestHistoricalData(account: number): Promise<void> {
    try {
      this.isLoadingResults = true;
      const response: HttpResponse<IHistoricalResponse> = await this.happylandService.requestHistoryInformationByCard(account);

      if (response.status == 200) {
        if (response.body.status) {
          // SAVE DATA
          this.cardActiveFrom = response.body.data.activeFrom;
          this.sourceETickets = response.body.data.historicalTickets;
          this.sourceGames = response.body.data.historicalGames;
          this.isLoadingResults = false;
        } else {
          // UNAUTHORIZED
          this.isDisabledByAdmin = true;
          this.isLoadingResults = false;
        }
      } else if (response.status === 403) {
        // Token inválido, solicitando nuevo token
        const tokenResponse = await this.happylandService.requestTokenJWT(account.toString());
        if (tokenResponse.status === 200) {
          sessionStorage.setItem('token', tokenResponse.body.token);
          // Volver a solicitar
          await this.requestHistoricalData(account);
        } else {
          this.helperService.redirectToCardErrorPage();
        }
      } else {
        // Error 500
        this.helperService.redirectToCardErrorPage();
      }
      return Promise.resolve();
    } catch (errCritical) {
      this.isLoadingResults = false;
      this.eventFailure = true;
    }
  }

  /**
   * Cierra el bottom sheet
   */
  close(): void {
    if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  }

  /**
   * Obtiene el texto a mostrar según el tipo de botón presionado
   */
  getDisplayText(): string {
    if (!this.data || !this.data.type) {
      return 'Información histórica';
    }

    switch (this.data.type) {
      case 'gameplay':
        return 'Presionaste: HISTORIAL JUGADAS';
      case 'tickets':
        return 'Presionaste: HISTORIAL E-TICKETS';
      default:
        return 'Información histórica';
    }
  }

  /**
   * Obtiene el color de fondo según el tipo de botón presionado
   */
  getBackgroundColor(): string {
    if (!this.data || !this.data.type) {
      return '#ffffff';
    }

    switch (this.data.type) {
      case 'gameplay':
        return '#c12e5d'; // Color para HISTORIAL JUGADAS
      case 'tickets':
        return '#ffd51b'; // Color para HISTORIAL E-TICKETS
      default:
        return '#ffffff';
    }
  }

  /**
   * Obtiene el color de fondo dinámico según la pestaña activa
   */
  getDynamicBackgroundColor(): string {
    // Tab 0 = MIS JUGADAS (gameplay), Tab 1 = MIS ETICKETS (tickets)
    return this.lastTabSelected === 0 ? '#c12e5d' : '#ffd51b';
  }

  /**
   * Obtiene el color de texto según la pestaña activa
   */
  getTextColor(): string {
    // Tab 0 = MIS JUGADAS (texto blanco), Tab 1 = MIS ETICKETS (texto negro)
    return this.lastTabSelected === 0 ? 'white' : 'black';
  }

  /**
   * Obtiene las clases para el filtro de íconos según la pestaña activa
   */
  getIconFilterClass(): string {
    return this.lastTabSelected === 0 ? 'white-filter' : 'black-filter';
  }
}
